"use client";

import { useState } from "react";
import { Link } from "@wc/i18n/navigation";
import { useTranslations } from "next-intl";
import { api } from "@wc/trpc/react";

export default function ForgotPasswordPage() {
  const t = useTranslations("forgotPassword");
  const tMetadata = useTranslations("metadata");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const requestMutation = api.passwordReset.request.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      setIsLoading(false);
    },
    onError: () => {
      setError(t("errorGeneric"));
      setIsLoading(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    requestMutation.mutate({ email: email.trim() });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div>
          <div className="flex flex-col items-center gap-2">
            {/* eslint-disable-next-line @next/next/no-img-element -- static SVG mark */}
            <img
              src="/icons/icon.svg"
              width={40}
              height={40}
              alt=""
              className="shrink-0"
            />
            <p className="text-lg font-semibold text-gray-900">
              {tMetadata("title")}
            </p>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t("title")}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t("subtitle")}
          </p>
        </div>

        {submitted ? (
          <div className="space-y-6">
            <div className="rounded-md border border-green-200 bg-green-50 p-4 text-center text-sm text-green-800">
              {t("successMessage")}
            </div>
            <div className="text-center">
              <Link
                href="/login"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                {t("backToLogin")}
              </Link>
            </div>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="sr-only">
                {t("emailLabel")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none sm:text-sm"
                placeholder={t("emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="text-center text-sm text-red-600">{error}</div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? t("submitting") : t("submit")}
              </button>
            </div>

            <div className="text-center text-sm">
              <Link
                href="/login"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                {t("backToLogin")}
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
